import React, { useState } from "react";
import { Calculator, Clock } from "lucide-react";
import { useLang } from "../LanguageContext";

const TUITION = {
  1: [3000, 3400, 3800, 4200, 4600, 5000],
  2: [5700, 6460, 7220, 7980, 8740, 9500],
  3: [8400, 9520, 10640, 11760, 12880, 14000],
};

const ALAE = {
  1: [25, 30, 35],
  2: [50, 60, 70],
  3: [75, 90, 105],
};

const tuitionBand = (qf) => {
  if (qf <= 650) return 0;
  if (qf <= 900) return 1;
  if (qf <= 1150) return 2;
  if (qf <= 1400) return 3;
  if (qf <= 1650) return 4;
  return 5;
};

const alaeBand = (qf) => {
  if (qf < 650) return 0;
  if (qf <= 1200) return 1;
  return 2;
};

const euro = (n) => `${n.toLocaleString("fr-FR")} €`;

export const FeesCalculators = () => {
  const { t } = useLang();
  const [qf, setQf] = useState("");
  const [children, setChildren] = useState(1);
  const [alaeQf, setAlaeQf] = useState("");
  const [slots, setSlots] = useState(1);

  const qfNum = qf === "" ? null : Math.max(0, Number(qf));
  const alaeQfNum = alaeQf === "" ? null : Math.max(0, Number(alaeQf));

  const annual = qfNum === null || Number.isNaN(qfNum) ? null : TUITION[children][tuitionBand(qfNum)];
  const monthly = annual === null ? null : Math.round(annual / 12);
  const alaeFee =
    alaeQfNum === null || Number.isNaN(alaeQfNum) ? null : ALAE[slots][alaeBand(alaeQfNum)];

  const qfField = (value, onChange, testid) => (
    <div>
      <label className="block text-sm font-medium text-ink mb-1.5">{t("calc.qf_label")}</label>
      <input
        type="number"
        min="0"
        step="1"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="field"
        placeholder={t("calc.qf_placeholder")}
        data-testid={testid}
      />
    </div>
  );

  const select = (label, value, onChange, options, testid) => (
    <div>
      <label className="block text-sm font-medium text-ink mb-1.5">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="field"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="card-soft p-7 sm:p-8" data-testid="fees-calculators">
      <p className="eyebrow mb-5">{t("calc.eyebrow")}</p>

      {/* Tuition fees */}
      <div data-testid="calc-tuition">
        <h3 className="font-heading text-lg sm:text-xl font-semibold text-ink flex items-start gap-2.5 mb-4">
          <Calculator size={19} className="text-forest mt-1 shrink-0" />
          <span>{t("calc.fees_title")}</span>
        </h3>

        <div className="space-y-4">
          {qfField(qf, setQf, "calc-tuition-qf")}
          {select(
            t("calc.children_label"),
            children,
            setChildren,
            [
              { value: 1, label: t("calc.child_one") },
              { value: 2, label: t("calc.child_two") },
              { value: 3, label: t("calc.child_three") },
            ],
            "calc-tuition-children"
          )}
        </div>

        <div className="mt-5 rounded-2xl bg-forest-light/50 border border-forest-light px-5 py-4">
          {annual === null ? (
            <p className="text-sm text-ink-muted" data-testid="calc-tuition-hint">
              {t("calc.qf_hint")}
            </p>
          ) : (
            <>
              <p className="text-xs uppercase tracking-wide text-forest-deep/70 mb-1">
                {t("calc.fees_result_label")}
              </p>
              <p
                className="font-heading text-2xl sm:text-3xl font-semibold text-forest-deep"
                data-testid="calc-tuition-annual"
              >
                {euro(annual)} <span className="text-base font-normal">{t("calc.per_year")}</span>
              </p>
              <p className="text-sm text-ink-muted mt-1.5" data-testid="calc-tuition-monthly">
                {t("calc.fees_monthly").replace("{x}", monthly.toLocaleString("fr-FR"))}
              </p>
            </>
          )}
        </div>
        <p className="text-xs text-ink-muted italic mt-3 leading-relaxed">« {t("calc.fees_note")} »</p>
      </div>

      <hr className="my-7 border-cream-300" />

      {/* ALAE */}
      <div data-testid="calc-alae">
        <h3 className="font-heading text-lg sm:text-xl font-semibold text-ink flex items-start gap-2.5 mb-4">
          <Clock size={19} className="text-forest mt-1 shrink-0" />
          <span>{t("calc.alae_title")}</span>
        </h3>

        <div className="space-y-4">
          {qfField(alaeQf, setAlaeQf, "calc-alae-qf")}
          {select(
            t("calc.slots_label"),
            slots,
            setSlots,
            [
              { value: 1, label: t("calc.slot_one") },
              { value: 2, label: t("calc.slot_two") },
              { value: 3, label: t("calc.slot_three") },
            ],
            "calc-alae-slots"
          )}
          <p className="text-xs text-ink-muted leading-relaxed">{t("calc.slots_hint")}</p>
        </div>

        <div className="mt-5 rounded-2xl bg-cream-100 border border-cream-300 px-5 py-4">
          {alaeFee === null ? (
            <p className="text-sm text-ink-muted" data-testid="calc-alae-hint">
              {t("calc.qf_hint")}
            </p>
          ) : (
            <>
              <p className="text-xs uppercase tracking-wide text-ink-muted mb-1">
                {t("calc.alae_result_label")}
              </p>
              <p
                className="font-heading text-2xl sm:text-3xl font-semibold text-forest-deep"
                data-testid="calc-alae-annual"
              >
                {euro(alaeFee)} <span className="text-base font-normal">{t("calc.per_year")}</span>
              </p>
            </>
          )}
        </div>
        <p className="text-xs text-ink-muted italic mt-3 leading-relaxed">« {t("calc.alae_note")} »</p>
      </div>
    </div>
  );
};

export default FeesCalculators;
